import {
	AppstoreOutlined,
	PlusOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Popconfirm, Table, Tabs, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FormController, TFormStructure } from "formifex";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import FormBuilderSaveButton from "../../../components/Button/FormBuilderSaveButton";
import CustomDrawer from "../../../components/CustomDrawer";
import { HomeContext } from "../../../containers/Home";
import {
	createRolePermission,
	deleteRolePermission,
	getRolePermissionsList,
	updateRolePermission,
} from "../../../services/api-server/roles_permissions";
import { useTenantStore } from "../../../stores/tenantStore";
import { useUserStore } from "../../../stores/userStore";
import { useEasterEgg } from "./useEasterEgg";

// Types
interface Role {
	_id: string;
	name: string;
	description: string;
	key:
		| "tenant_admin"
		| "tenant_member"
		| "project_manager"
		| "team_member"
		| "general_member"
		| "well_examiner";
	scope: "tenant" | "project";
	lastUpdated: string;
	permissions?: string[];
}

// Permission structure
interface PermissionChild {
	key: string;
	label: string;
}

interface PermissionGroup {
	key: string;
	label: string;
	children: PermissionChild[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
	{
		key: "Project",
		label: "Project",
		children: [
			{ key: "View.Project", label: "View" },
			{ key: "Create.Project", label: "Create" },
			{ key: "Edit.Project", label: "Edit" },
			{ key: "Delete.Project", label: "Delete" },
		],
	},
	{
		key: "Well",
		label: "Well",
		children: [
			{ key: "View.Well", label: "View" },
			{ key: "Create.Well", label: "Create" },
			{ key: "Edit.Well", label: "Edit" },
			{ key: "Delete.Well", label: "Delete" },
		],
	},
	{
		key: "Operation",
		label: "Operation",
		children: [
			{ key: "View.Operation", label: "View" },
			{ key: "Create.Operation", label: "Create" },
			{ key: "Edit.Operation", label: "Edit" },
			{ key: "Operational Update.Operation", label: "Operational Update" },
			{ key: "Delete.Operation", label: "Delete" },
		],
	},
];

interface RoleFormData {
	name: string;
	description?: string;
	key:
		| "tenant_admin"
		| "tenant_member"
		| "project_manager"
		| "team_member"
		| "general_member"
		| "well_examiner";
	scope?: "tenant" | "project";
}

interface SidePanelProps {
	isOpen: boolean;
	title: string;
	children: React.ReactNode;
	footer?: React.ReactNode;
	onClose: () => void;
}

// SidePanel Component
const SidePanel: React.FC<SidePanelProps> = ({
	isOpen,
	title,
	children,
	footer,
	onClose,
}) => {
	if (!isOpen) return null;

	return (
		<div className="flex flex-1 flex-col rounded-sm bg-(--side-container-bg)!">
			{/* Header */}
			<div className="generic-header h-[50px]!">
				<span className="font-medium">{title}</span>
			</div>

			{/* Body */}
			<div className="flex-1 overflow-auto p-4">{children}</div>

			{/* Footer */}
			{footer && <div className="generic-footer h-[50px]!">{footer}</div>}
		</div>
	);
};

// PermissionsDrawer Component
interface PermissionsDrawerProps {
	open: boolean;
	onClose: () => void;
	role: Role | null;
	onSave: (permissions: string[]) => void;
	isSaving?: boolean;
}

const PermissionsDrawer: React.FC<PermissionsDrawerProps> = ({
	open,
	onClose,
	role,
	onSave,
	isSaving,
}) => {
	const [checkedPermissions, setCheckedPermissions] = useState<string[]>([]);

	useEffect(() => {
		if (open) {
			setCheckedPermissions(role?.permissions || []);
		}
	}, [open, role]);

	const isGroupChecked = (group: PermissionGroup) => {
		const allKeys = group.children.map((c) => c.key);
		return (
			allKeys.length > 0 && allKeys.every((k) => checkedPermissions.includes(k))
		);
	};

	const isGroupIndeterminate = (group: PermissionGroup) => {
		const allKeys = group.children.map((c) => c.key);
		const someChecked = allKeys.some((k) => checkedPermissions.includes(k));
		return someChecked && !isGroupChecked(group);
	};

	const handleGroupChange = (group: PermissionGroup, checked: boolean) => {
		const allKeys = group.children.map((c) => c.key);
		if (checked) {
			setCheckedPermissions((prev) => [...new Set([...prev, ...allKeys])]);
		} else {
			setCheckedPermissions((prev) => prev.filter((k) => !allKeys.includes(k)));
		}
	};

	const handlePermissionChange = (key: string, checked: boolean) => {
		if (checked) {
			setCheckedPermissions((prev) => [...new Set([...prev, key])]);
		} else {
			setCheckedPermissions((prev) => prev.filter((k) => k !== key));
		}
	};

	return (
		<CustomDrawer
			title={`Permission settings (${role?.name || ""})`}
			open={open}
			close={onClose}
			footer={
				<div className="flex justify-end gap-2">
					<Button onClick={onClose}>Cancel</Button>
					<Button
						type="primary"
						loading={isSaving}
						onClick={() => onSave(checkedPermissions)}
						className="bg-[#1a1f37]"
					>
						Save
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-4 w-full">
				<p
					className={`text-sm text-(${localStorage.getItem("theme") === "dark" ? "white" : "black"})`}
				>
					Select a role to customise its access to features and data.
				</p>

				<div className="border border-(--generic-table-border-color) rounded overflow-hidden">
					{/* Table Header */}
					<div className="flex border-b border-(--generic-table-border-color) bg-(--generic-table-header-bg-color)">
						<div className="w-[40%] px-4 py-2 font-semibold text-sm text-(--generic-table-header-text-color) border-r border-(--generic-table-border-color)">
							Features
						</div>
						<div className="flex-1 px-4 py-2 font-semibold text-sm text-(--generic-table-header-text-color)">
							Permissions
						</div>
					</div>

					{/* Permission Groups */}
					{PERMISSION_GROUPS.map((group, idx) => (
						<div
							key={group.key}
							className={`flex ${idx < PERMISSION_GROUPS.length - 1 ? "border-b border-(--generic-table-border-color)" : ""}`}
						>
							{/* Feature Name */}
							<div className="w-[40%] px-4 py-3 border-r border-(--generic-table-border-color) flex items-start bg-(--generic-table-body-bg-color)">
								<span className="text-sm font-medium text-(--generic-table-body-text-color)">
									{group.label}
								</span>
							</div>

							{/* Permissions */}
							<div className="flex-1 px-4 py-3 flex flex-col gap-2 bg-(--generic-table-body-bg-color)">
								{/* Group parent checkbox */}
								<Checkbox
									checked={isGroupChecked(group)}
									indeterminate={isGroupIndeterminate(group)}
									onChange={(e) => handleGroupChange(group, e.target.checked)}
									className="font-semibold text-(--generic-table-body-text-color)"
								>
									{group.label}
								</Checkbox>

								{/* Child permissions */}
								<div className="ml-6 flex flex-col gap-1.5">
									{group.children.map((child) => (
										<Checkbox
											key={child.key}
											checked={checkedPermissions.includes(child.key)}
											onChange={(e) =>
												handlePermissionChange(child.key, e.target.checked)
											}
										>
											{child.label}
										</Checkbox>
									))}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</CustomDrawer>
	);
};

// RoleForm Component
interface RoleFormProps {
	initialValues?: Role | null;
	existingRoles: Role[];
	onSave: (values: RoleFormData) => void;
	onDelete: (id: string) => void;
	onCancel: () => void;
	onSavePermissions: (permissions: string[]) => void;
	isSavingPermissions?: boolean;
	renderFooter?: (footerContent: React.ReactNode) => void;
}

const RoleForm: React.FC<RoleFormProps> = ({
	initialValues,
	existingRoles,
	onSave,
	onDelete,
	onCancel,
	onSavePermissions,
	isSavingPermissions,
	renderFooter,
}) => {
	const form = FormController.useFormController();
	const isEdit = !!initialValues;
	const context: any = useContext(HomeContext);
	const [permissionsDrawerOpen, setPermissionsDrawerOpen] = useState(false);

	// All available role key options
	const allRoleKeyOptions = [
		{ label: "Tenant Admin", value: "tenant_admin" },
		{ label: "Tenant Member", value: "tenant_member" },
		{ label: "Project Manager", value: "project_manager" },
		{ label: "Team Member", value: "team_member" },
		{ label: "General Member", value: "general_member" },
	];

	// Filter out role keys that already exist (except for the current editing role)
	const availableRoleKeyOptions = useMemo(() => {
		const existingKeys = existingRoles
			.filter((role) => !initialValues || role._id !== initialValues._id)
			.map((role) => role.key);
		return allRoleKeyOptions.filter(
			(option) => !existingKeys.includes(option.value as Role["key"])
		);
	}, [existingRoles, initialValues]);

	// Define form structure
	const formSchema: TFormStructure = {
		id: "role_form",
		layout: "vertical",
		requiredMark: "optional",
		items: [
			{
				label: "Role name",
				id: "name",
				type: "input",
				dataType: "string",
				inputType: "string",
				required: true,
				placeholder: "Enter role name",
			},
			{
				label: "Description",
				id: "description",
				type: "input",
				dataType: "string",
				inputType: "textarea",
				required: false,
				placeholder: "Enter description",
				properties: {
					rows: 4,
				},
			},
			{
				label: "Role key",
				id: "key",
				type: "input",
				dataType: "string",
				inputType: "select",
				required: true,
				options: availableRoleKeyOptions,
				placeholder: "Select role key",
				disabled: isEdit, // Disable when editing existing role
			},
		],
	};

	// Initialize form with schema and data
	useEffect(() => {
		form.setSchema(formSchema);

		if (initialValues) {
			form.setInitialFormData({
				name: initialValues.name,
				description: initialValues.description,
				key: initialValues.key,
			});
		} else {
			form.setInitialFormData({
				name: "",
				description: "",
				key: "",
			});
		}
	}, [initialValues, availableRoleKeyOptions]);

	// Handle form dirty state
	useEffect(() => {
		if (form.isDirty) {
			context?.handleFormUpdating(true, "module-roles");
		}
		return () => {
			context?.handleFormUpdating(false, "module-roles");
		};
	}, [form.isDirty, context]);

	// Communicate footer content to parent when form state changes
	useEffect(() => {
		if (renderFooter) {
			const footerContent = (
				<>
					<div className="flex items-center gap-2">
						{isEdit && (
							<Popconfirm
								title="Delete role"
								description="Are you sure you want to delete this role?"
								onConfirm={handleDelete}
								okText="Delete"
								okType="danger"
								cancelText="Cancel"
							>
								<Button danger>Delete</Button>
							</Popconfirm>
						)}
					</div>
					<div className="flex items-center gap-2">
						{form.isDirty ? (
							<Popconfirm
								title="Unsaved Changes"
								description="You have unsaved changes. Discard them?"
								onConfirm={onCancel}
								okText="Discard"
								cancelText="Continue editing"
							>
								<Button>Cancel</Button>
							</Popconfirm>
						) : (
							<Button onClick={onCancel}>Cancel</Button>
						)}
						<FormBuilderSaveButton form={form} handleFormSave={handleSave} />
					</div>
				</>
			);
			renderFooter(footerContent);
		}
	}, [form.isDirty, isEdit]);

	const handleSave = useCallback(() => {
		// Read current values directly from the antd form ref to avoid debounced stale data
		const values = form.formRef?.getFieldsValue?.() || form.formData;
		onSave(values as RoleFormData);
		context?.handleFormUpdating(false, "module-roles");
	}, [form.formRef, form.formData, onSave, context]);

	const handleDelete = useCallback(() => {
		if (initialValues) {
			onDelete(initialValues._id);
			context?.handleFormUpdating(false, "module-roles");
		}
	}, [initialValues, onDelete, context]);

	return (
		<div className="flex flex-col h-full">
			<div className="flex-1">
				<FormController
					formController={form}
					theme={"dark"}
					enablePostSave={false}
					resetOnDependencyChange={true}
					resetOnDependencyEmpty={true}
				/>
			</div>

			{isEdit && (
				<div>
					<Button
						className="px-0!"
						icon={<SettingOutlined />}
						type="link"
						onClick={() => setPermissionsDrawerOpen(true)}
					>
						Manage Permissions
					</Button>
				</div>
			)}

			<PermissionsDrawer
				open={permissionsDrawerOpen}
				onClose={() => setPermissionsDrawerOpen(false)}
				role={initialValues || null}
				onSave={(permissions) => {
					onSavePermissions(permissions);
					setPermissionsDrawerOpen(false);
				}}
				isSaving={isSavingPermissions}
			/>
		</div>
	);
};

// Main Component
function ModuleRoles() {
	const [activeScope, setActiveScope] = useState<"all" | "tenant" | "project">(
		"all"
	);
	const [searchRole, setSearchRole] = useState("");
	const [searchDescription, setSearchDescription] = useState("");
	const [sidePanelOpen, setSidePanelOpen] = useState(false);
	const [sidePanelFooter, setSidePanelFooter] = useState<React.ReactNode>(null);
	const [editingRole, setEditingRole] = useState<Role | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(20);

	// Get tenant key from navigation state (admin configuring other tenant) or global store
	const location = useLocation();
	const tenantFromState = location.state?.tenantRoleKey;
	const tenantFromStore = useTenantStore((state) => state.tenant);
	const tenantKey = tenantFromState || tenantFromStore?.role_key;

	// Get user from store
	const user = useUserStore((state) => state.user);

	// React Query setup
	const queryClient = useQueryClient();

	// Fetch roles using useQuery
	const {
		data: rolesData,
		isLoading,
		isError,
		error,
	} = useQuery({
		queryKey: ["roles", tenantKey],
		queryFn: () => {
			console.log("Fetching roles for tenant:", tenantKey);
			return getRolePermissionsList(tenantKey);
		},
		enabled: !!tenantKey,
	});

	// Show error message if query fails
	useEffect(() => {
		if (isError && error) {
			console.error("Error fetching roles:", error);
			message.error("Failed to fetch roles");
		}
	}, [isError, error]);

	// Debug logging
	useEffect(() => {
		console.log("tenantKey:", tenantKey);
		console.log("tenantFromStore:", tenantFromStore);
		console.log("rolesData:", rolesData);
	}, [tenantKey, tenantFromStore, rolesData]);

	// Format roles data
	const roles = useMemo(() => {
		console.log("Formatting roles data:", rolesData);
		if (!rolesData || !Array.isArray(rolesData)) {
			console.log("No roles data or not an array");
			return [];
		}
		return rolesData.map((item: any) => ({
			_id: item._id,
			name: item.name,
			description: item.description || "",
			key: item.key,
			scope:
				item.scope ||
				(item.key === "tenant_admin" || item.key === "tenant_member"
					? "tenant"
					: "project"),
			permissions: item.permissions || [],
			lastUpdated: item.modified_on
				? new Date(item.modified_on).toLocaleString("en-GB", {
						day: "2-digit",
						month: "short",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
						hour12: true,
					})
				: new Date().toLocaleString("en-GB", {
						day: "2-digit",
						month: "short",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
						hour12: true,
					}),
		}));
	}, [rolesData]);

	// Create mutation
	const createMutation = useMutation({
		mutationFn: (data: RoleFormData) => {
			const inferredScope =
				data.key === "tenant_admin" || data.key === "tenant_member"
					? "tenant"
					: "project";
			return createRolePermission(tenantKey, {
				data: {
					name: data.name,
					description: data.description || "",
					key: data.key,
					scope: inferredScope,
					permissions: [],
				},
				user: {
					name: user?.name || user?.displayName || "Unknown",
					email: user?.email || "",
				},
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["roles", tenantKey] });
			message.success("Role created successfully");
			setSidePanelOpen(false);
			setEditingRole(null);
		},
		onError: (error: any) => {
			console.error("Error creating role:", error);
			message.error(error?.message || "Failed to create role");
		},
	});

	// Update mutation
	const updateMutation = useMutation({
		mutationFn: (data: { id: string; values: RoleFormData }) =>
			updateRolePermission(tenantKey, {
				filter: { _id: data.id },
				data: {
					$set: {
						name: data.values.name,
						description: data.values.description || "",
						key: data.values.key,
					},
				},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["roles", tenantKey] });
			message.success("Role updated successfully");
			setSidePanelOpen(false);
			setEditingRole(null);
		},
		onError: (error: any) => {
			console.error("Error updating role:", error);
			message.error(error?.message || "Failed to update role");
		},
	});

	// Delete mutation
	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteRolePermission(tenantKey, id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["roles", tenantKey] });
			message.success("Role deleted successfully");
			setSidePanelOpen(false);
			setEditingRole(null);
		},
		onError: (error: any) => {
			console.error("Error deleting role:", error);
			message.error(error?.message || "Failed to delete role");
		},
	});

	// Permissions mutation
	const permissionsMutation = useMutation({
		mutationFn: (data: { id: string; permissions: string[] }) =>
			updateRolePermission(tenantKey, {
				filter: { _id: data.id },
				data: {
					$set: {
						permissions: data.permissions,
					},
				},
			}),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["roles", tenantKey] });
			message.success("Permissions updated successfully");
			// Update local editingRole so PermissionsDrawer shows new values immediately
			setEditingRole((prev) =>
				prev && prev._id === variables.id
					? { ...prev, permissions: variables.permissions }
					: prev
			);
		},
		onError: (error: any) => {
			console.error("Error updating permissions:", error);
			message.error(error?.message || "Failed to update permissions");
		},
	});

	// Filtered roles based on search
	const filteredRoles = useMemo(() => {
		console.log("Filtering roles:", roles.length);
		return roles.filter((role) => {
			const matchesName = role.name
				.toLowerCase()
				.includes(searchRole.toLowerCase());
			const matchesDesc = role.description
				.toLowerCase()
				.includes(searchDescription.toLowerCase());
			return matchesName && matchesDesc;
		});
	}, [roles, searchRole, searchDescription]);

	const handleAddClick = useCallback(() => {
		setEditingRole(null);
		setSidePanelOpen(true);
	}, []);

	const handleEditClick = useCallback((role: Role) => {
		setEditingRole(role);
		setSidePanelOpen(true);
	}, []);

	const handleCloseSidePanel = useCallback(() => {
		setSidePanelOpen(false);
		setEditingRole(null);
		setSidePanelFooter(null);
	}, []);

	const handleSaveRole = useCallback(
		(values: RoleFormData) => {
			if (!tenantKey) {
				message.error("Tenant key not found");
				return;
			}

			if (editingRole) {
				// Update existing role
				updateMutation.mutate({ id: editingRole._id, values });
			} else {
				// Create new role
				createMutation.mutate(values);
			}
		},
		[editingRole, tenantKey]
	);

	const handleDeleteRole = useCallback(
		(id: string) => {
			if (!tenantKey) {
				message.error("Tenant key not found");
				return;
			}

			console.log("Deleting role:", id);

			deleteMutation.mutate(id);
		},
		[tenantKey]
	);

	const handleSavePermissions = useCallback(
		(permissions: string[]) => {
			if (!editingRole) {
				message.error("No role selected");
				return;
			}
			if (!tenantKey) {
				message.error("Tenant key not found");
				return;
			}
			permissionsMutation.mutate({ id: editingRole._id, permissions });
		},
		[editingRole, tenantKey]
	);

	// Check if any mutation is pending
	const isMutationPending =
		createMutation.isPending ||
		updateMutation.isPending ||
		deleteMutation.isPending ||
		permissionsMutation.isPending;

	const sidebarCategories = useMemo(() => {
		return [{ key: "all", label: "Role Management", count: roles.length }];
	}, [roles]);

	const columns: ColumnsType<Role> = [
		{
			title: "Role name",
			dataIndex: "name",
			key: "name",
			width: 200,
			onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
			render: (text: string, record: Role) => (
				<a
					onClick={() => handleEditClick(record)}
					className="text-[#00b4d8] hover:underline font-medium cursor-pointer"
				>
					{text}
				</a>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
			render: (text: string) => text || "-",
		},
		{
			title: "Role key",
			dataIndex: "key",
			key: "key",
			width: 150,
			onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
			render: (text: string) => {
				const roleKeyLabels: Record<string, string> = {
					tenant_admin: "Tenant Admin",
					tenant_member: "Tenant Member",
					project_manager: "Project Manager",
					team_member: "Team Member",
					general_member: "General Member",
				};
				const roleKeyColors: Record<string, string> = {
					tenant_admin: "red",
					tenant_member: "orange",
					project_manager: "blue",
					team_member: "green",
					general_member: "default",
				};
				return (
					<Tag color={roleKeyColors[text] || "default"}>
						{roleKeyLabels[text] || text}
					</Tag>
				);
			},
		},
	];

	const showEasterEgg = useEasterEgg({ secretWord: "pikachu" });

	return (
		<div className="generic-container">
			<div className="flex flex-1 flex-row gap-5 max-h-full h-full overflow-auto">
				<div className="flex flex-1 flex-row gap-5 overflow-hidden max-h-full">
					<div className=" flex flex-col w-[70%] max-h-full overflow-hidden rounded-sm">
						<div className="generic-header h-[52px]!">Module Roles</div>
						{showEasterEgg ? (
							/* Show this if user click 3 mouse 3 times on the screen */
							<div className="flex items-center justify-center flex-1 p-4">
								<img
									src="https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png"
									alt="Pikachu"
									className="w-140 h-140 object-contain image-rendering-pixelated"
									style={{ imageRendering: "pixelated" }}
								/>
							</div>
						) : (
							/* Normal content */
							<div className="generic-content h-full overflow-hidden gap-0!">
								<Tabs
									style={{ lineHeight: "1.55", height: "100%" }}
									tabPosition="left"
									onChange={(key: string) =>
										setActiveScope(key as "all" | "tenant" | "project")
									}
									activeKey={activeScope}
									items={sidebarCategories}
								/>
								<div className="flex flex-1 flex-col h-full overflow-hidden">
									{/* Content Header */}
									<div className="flex items-center justify-between px-4 py-3 w-full bg-(--generic-table-header-bg-color)!">
										<h2 className="font-medium">Role Management</h2>
										<Button
											// type="primary"
											icon={<PlusOutlined />}
											onClick={handleAddClick}
											disabled={sidePanelOpen}
											className="flex items-center gap-1 bg-[#1a1f37]"
										>
											Add
										</Button>
									</div>

									{/* Roles Table */}
									<Table
										rootClassName="custom-table"
										rowClassName={(record: Role) => {
											let className = "";
											if (record._id === editingRole?._id) {
												className += " antd-table-row-active";
											}
											return className;
										}}
										onRow={(record: Role) => ({
											onClick: () => handleEditClick(record),
										})}
										columns={columns}
										dataSource={filteredRoles}
										rowKey="_id"
										loading={isLoading || isMutationPending}
										pagination={{
											current: currentPage,
											pageSize: pageSize,
											total: filteredRoles.length,
											onChange: (page) => setCurrentPage(page),
											showSizeChanger: true,
											pageSizeOptions: ["20", "50", "100"],
											position: ["bottomCenter"],
										}}
										scroll={{ y: "calc(100vh - 380px)" }}
									/>
								</div>
							</div>
						)}
					</div>

					{/* Right Side Panel */}
					<SidePanel
						isOpen={sidePanelOpen}
						title={editingRole ? "Role Management Form" : "Add New Role"}
						onClose={handleCloseSidePanel}
						footer={
							<div className="flex items-center justify-between w-full">
								{sidePanelFooter}
							</div>
						}
					>
						<RoleForm
							initialValues={editingRole}
							existingRoles={roles}
							onSave={handleSaveRole}
							onDelete={handleDeleteRole}
							onCancel={handleCloseSidePanel}
							onSavePermissions={handleSavePermissions}
							isSavingPermissions={permissionsMutation.isPending}
							renderFooter={setSidePanelFooter}
						/>
					</SidePanel>
				</div>
			</div>
		</div>
	);
}

export default ModuleRoles;
